import { computed } from 'vue'
import { usePropertyDetailQuery } from '../api/usePropertyDetailQuery'
import {
  DEFAULT_PROPERTY_PROCESS_STEPS,
  DEFAULT_PROPERTY_FAQS,
} from '../constants/propertyDefaults'
import {
  PROPERTY_PLACEHOLDER_IMAGE,
  buildAbout,
  buildAmenities,
  buildCalculator,
  buildDocuments,
  buildFinancialSections,
  buildFinancials,
  buildGallery,
  buildHeader,
  buildHighlights,
  buildLocation,
  type PropertyDetailData,
} from './propertyDetailBuilders'

// Subscribes to the detail query and exposes the page's per-section view
// models. Stateless — no refs, no mutations. For bookmark state, compose
// with usePropertyBookmark in the same container.
export const usePropertyDetailViewModel = (propertyId: string) => {
  const query = usePropertyDetailQuery(propertyId)
  const raw = computed<PropertyDetailData | null>(() => query.data.value ?? null)
  const loading = computed(() => query.isLoading.value)
  const error = computed(() => query.error.value?.message ?? null)
  const offeringId = computed(() => raw.value?.offeringId ?? '')

  const processSteps = computed(() => DEFAULT_PROPERTY_PROCESS_STEPS)
  const faqs = computed(() => DEFAULT_PROPERTY_FAQS)

  const gallery = computed(() => buildGallery(raw.value))
  const header = computed(() => buildHeader(raw.value))
  const financials = computed(() => buildFinancials(raw.value))
  const financialSections = computed(() => buildFinancialSections(raw.value?.financials))
  const about = computed(() => buildAbout(raw.value))
  const amenities = computed(() => buildAmenities(raw.value))
  const highlights = computed(() => buildHighlights(raw.value))
  const calculator = computed(() => buildCalculator(raw.value))
  const documents = computed(() => buildDocuments(raw.value))
  const location = computed(() => buildLocation(raw.value))

  return {
    raw,
    loading,
    error,
    offeringId,
    gallery,
    header,
    financials,
    financialSections,
    about,
    amenities,
    highlights,
    calculator,
    location,
    documents,
    processSteps,
    faqs,
    propertyPlaceholder: PROPERTY_PLACEHOLDER_IMAGE,
  }
}
